// EditNotificationModal.jsx
import React from "react";
import styles from "./Modal.module.scss";
import { MdClose } from "react-icons/md";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { Select } from "antd";

const { Option } = Select;

export default function EditNotificationModal({ data, setOpen, reloadData }) {
  const close = () => setOpen(null);

  // Helper to map expiresAt back to one of our keys (optional)
  const computeInitialExpiry = (expiresAt) => {
    if (!expiresAt) return "24hrs";
    const then = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    const diff = then.getTime() - Date.now();
    const hours = diff / (1000 * 60 * 60);
    if (hours < 36) return "24hrs";
    if (hours < 72) return "48hrs";
    if (hours < 24 * 7 + 12) return "1 Week";
    if (hours < 24 * 30 + 12) return "1 Month";
    if (hours < 24 * 365 + 12) return "1 Year";
    return "Indefinitely";
  };

  const accountOptions = [
    { label: "Admin", value: "admin" },
    { label: "Super Admin", value: "super-admin" },
    { label: "Executive", value: "executive" },
    { label: "NCAA", value: "ncaa" },
  ];

  const formik = useFormik({
    initialValues: {
      title: data.title || "",
      message: data.message || "",
      targetAccounts: data.targetAccounts || [],
      expiry: computeInitialExpiry(data.expiresAt),
    },
    validate: (values) => {
      const errors = {};
      if (!values.title) errors.title = "Title is required";
      if (!values.message) errors.message = "Message is required";
      if (!values.targetAccounts.length)
        errors.targetAccounts = "Select at least one account";
      if (!values.expiry) errors.expiry = "Please select an expiration";
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const now = new Date();
        let expiresAt;
        switch (values.expiry) {
          case "24hrs":
            expiresAt = new Date(now.getTime() + 24 * 60 * 60 * 1000);
            break;
          case "48hrs":
            expiresAt = new Date(now.getTime() + 48 * 60 * 60 * 1000);
            break;
          case "1 Week":
            expiresAt = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            break;
          case "1 Month":
            expiresAt = new Date(now.setMonth(now.getMonth() + 1));
            break;
          case "1 Year":
            expiresAt = new Date(now.setFullYear(now.getFullYear() + 1));
            break;
          case "Indefinitely":
          default:
            expiresAt = new Date(9999, 11, 31);
        }

        await db
          .collection("notification-banner")
          .doc(data.id)
          .update({
            title: values.title,
            message: values.message,
            targetAccounts: values.targetAccounts,
            expiresAt,
            updatedAt: new Date(),
          });

        toast.success("Notification updated successfully!", { theme: "colored" });
        reloadData();
        close();
      } catch (error) {
        console.error("Error updating notification: ", error);
        toast.error("Error updating notification", { theme: "colored" });
      }
    },
  });

  return (
    <div className={styles.modalContainer}>
      <div onClick={close} className={styles.clickAway} />
      <div className={styles.modal}>
        <button onClick={close} className={styles.closeButton}>
          <MdClose />
        </button>
        <form onSubmit={formik.handleSubmit}>
          <div className={styles.content}>
            <h3>Edit Notification</h3>
            <span>Update the details of the notification.</span>
            <hr />
            <div className={styles.inputs}>
              {/* Title */}
              <label htmlFor="title">Title</label>
              <input
                id="title"
                name="title"
                type="text"
                placeholder="Enter notification title"
                onChange={formik.handleChange}
                value={formik.values.title}
              />
              {formik.errors.title && (
                <span className={styles.error}>{formik.errors.title}</span>
              )}

              {/* Message */}
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                placeholder="Enter notification message (max 1000 chars)"
                rows={4}
                maxLength={1000}
                onChange={formik.handleChange}
                value={formik.values.message}
              />
              {formik.errors.message && (
                <span className={styles.error}>{formik.errors.message}</span>
              )}

              {/* Target Accounts Multi‑Select */}
              <label htmlFor="targetAccounts">Target Accounts</label>
              <Select
                id="targetAccounts"
                mode="multiple"
                placeholder="Select target accounts"
                options={accountOptions}
                value={formik.values.targetAccounts}
                onChange={(vals) =>
                  formik.setFieldValue("targetAccounts", vals)
                }
                style={{ width: "100%", marginBottom: 8 }}
              />
              {formik.errors.targetAccounts && (
                <span className={styles.error}>
                  {formik.errors.targetAccounts}
                </span>
              )}

              {/* Expiration */}
              <label htmlFor="expiry">Expires In</label>
              <select
                id="expiry"
                name="expiry"
                onChange={formik.handleChange}
                value={formik.values.expiry}
              >
                <option value="24hrs">24hrs</option>
                <option value="48hrs">48hrs</option>
                <option value="1 Week">1 Week</option>
                <option value="1 Month">1 Month</option>
                <option value="1 Year">1 Year</option>
                <option value="Indefinitely">Indefinitely</option>
              </select>
              {formik.errors.expiry && (
                <span className={styles.error}>{formik.errors.expiry}</span>
              )}
            </div>
          </div>
          <div className={styles.btns}>
            <button type="button" onClick={close}>
              Cancel
            </button>
            <button type="submit">Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
